/**
 * 工艺路线常量定义
 * 
 * @description 定义工艺路线相关的常量映射（枚举值 → 中文标签）
 * @module types/process-route/constants
 * @category ProcessRoute - 工艺路线管理
 * 
 * @backend-source
 * - Model: backend/app/Modules/ProcessRoute/Models/ProcessRoute.php
 *   - $routeTypeMap (line 105-110)
 *   - $statusMap (line 123-128)
 * 
 * @update-trigger
 * - Model 静态属性 $routeTypeMap 或 $statusMap 变更时
 * - 新增枚举类型时
 * 
 * @validation
 * ```bash
 * # 验证方法：对照 Model 的静态属性
 * 1. 确保每个枚举都有对应的 _LABELS 常量
 * 2. 标签文本必须与后端完全一致
 * 3. 枚举值必须完整覆盖
 * ```
 * 
 * @usage
 * ```typescript
 * import { PROCESS_ROUTE_TYPE_LABELS } from '@bayes-mes/shared';
 * 
 * // 获取路线类型的中文标签
 * const label = PROCESS_ROUTE_TYPE_LABELS[ProcessRouteType.STANDARD]; // "标准工艺"
 * ```
 * 
 * @last-updated 2025-10-18
 * @status ✅ 已验证
 */

import { ProcessRouteType, ProcessRouteStatus } from './enums';

/**
 * 工艺路线类型标签映射
 * 
 * @backend ProcessRoute::$routeTypeMap (line 105-110)
 */
export const PROCESS_ROUTE_TYPE_LABELS: Record<ProcessRouteType, string> = {
  [ProcessRouteType.STANDARD]: '标准工艺',
  [ProcessRouteType.ALTERNATIVE]: '备选工艺',
  [ProcessRouteType.TRIAL]: '试制工艺',
  [ProcessRouteType.OBSOLETE]: '淘汰工艺',
};

/**
 * 工艺路线状态标签映射
 * 
 * @backend ProcessRoute::$statusMap (line 123-128)
 */
export const PROCESS_ROUTE_STATUS_LABELS: Record<ProcessRouteStatus, string> = {
  [ProcessRouteStatus.DRAFT]: '草稿',
  [ProcessRouteStatus.ACTIVE]: '生效',
  [ProcessRouteStatus.FROZEN]: '冻结',
  [ProcessRouteStatus.OBSOLETE]: '淘汰',
};

/**
 * 工艺路线类型选项（用于下拉选择）
 */
export const PROCESS_ROUTE_TYPE_OPTIONS = Object.entries(PROCESS_ROUTE_TYPE_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
);

/**
 * 工艺路线状态选项（用于下拉选择）
 */
export const PROCESS_ROUTE_STATUS_OPTIONS = Object.entries(PROCESS_ROUTE_STATUS_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
);

